import { useState } from "react";
import {
  Box, Paper, Stack, TextField, Button, Alert, MenuItem, Typography, IconButton,
  InputAdornment, Tooltip, Grid2 as Grid,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { diagnosticsApi } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { PageHeader } from "@/components/PageHeader";
import { useVoiceDictation } from "./useVoiceDictation";

/**
 * Radiology reporting — the per-modality template master together with authoring, approval and
 * addenda. Templates live here because their only consumer is the report editor below them.
 */
export function RadiologyReportingPage() {
  const err = (...ms: { isError: boolean; error: unknown }[]) =>
    ms.filter(m => m.isError).map((m, i) => <Alert key={i} severity="error" sx={{ mt: 1 }}>{errorMessage(m.error)}</Alert>);

  const qc = useQueryClient();
  const canManageTemplates = useAuth(s => s.hasPermission("diagnostics.catalog.manage"));
  const canDraft = useAuth(s => s.hasPermission("diagnostics.report.draft"));
  const canApprove = useAuth(s => s.hasPermission("diagnostics.report.approve"));
  const canAmend = useAuth(s => s.hasPermission("diagnostics.report.amend"));

  const modalities = useQuery({ queryKey: ["dx-modalities"], queryFn: () => diagnosticsApi.listModalities() });
  const templates = useQuery({ queryKey: ["dx-rad-report-templates"], queryFn: () => diagnosticsApi.listRadReportTemplates() });

  // ---- template master ----
  const [tmMod, setTmMod] = useState(""); const [tmCode, setTmCode] = useState(""); const [tmName, setTmName] = useState("");
  const [tmTech, setTmTech] = useState(""); const [tmFind, setTmFind] = useState(""); const [tmImp, setTmImp] = useState("");
  const createTemplate = useMutation({
    meta: { successMessage: "Report template created" },
    mutationFn: () => diagnosticsApi.createRadReportTemplate({
      modalityId: tmMod, code: tmCode.trim(), name: tmName.trim(),
      technique: tmTech || null, findings: tmFind || null, impression: tmImp || null,
    }),
    onSuccess: () => {
      setTmCode(""); setTmName(""); setTmTech(""); setTmFind(""); setTmImp("");
      qc.invalidateQueries({ queryKey: ["dx-rad-report-templates"] });
    },
  });

  // ---- report authoring ----
  const [rStudy, setRStudy] = useState(""); const [technique, setTechnique] = useState("");
  const [findings, setFindings] = useState(""); const [impression, setImpression] = useState("");
  const [templateId, setTemplateId] = useState("");
  const voice = useVoiceDictation();
  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const t = templates.data?.find(x => x.id === id);
    if (t) { setTechnique(t.technique ?? ""); setFindings(t.findings ?? ""); setImpression(t.impression ?? ""); }
  };
  const draft = useMutation({
    meta: { successMessage: "Report drafted" },
    mutationFn: () => diagnosticsApi.draftReport({ studyId: rStudy, technique: technique || null, findings, impression }),
  });

  const [reportId, setReportId] = useState(""); const [addText, setAddText] = useState("");
  const approve = useMutation({ meta: { successMessage: "Report approved" }, mutationFn: () => diagnosticsApi.approveReport(reportId, {}) });
  const addendum = useMutation({ meta: { successMessage: "Addendum added" }, mutationFn: () => diagnosticsApi.addReportAddendum(reportId, { text: addText }) });

  const micAdornment = (field: string, append: (t: string) => void) => voice.supported ? (
    <InputAdornment position="end">
      <Tooltip title={voice.listeningField === field ? "Stop dictation" : "Dictate"}>
        <IconButton size="small" color={voice.listeningField === field ? "error" : "default"}
          onClick={() => voice.listeningField === field ? voice.stop() : voice.start(field, append)}>
          {voice.listeningField === field ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </InputAdornment>
  ) : undefined;

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Diagnostics", "Radiology"]}
        title="Reporting"
        subtitle="Per-modality templates, report authoring, approval and addenda."
      />
      <Grid container spacing={2}>
        {canManageTemplates && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Report templates (per modality)</Typography>
              <Grid container spacing={1}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Stack spacing={1}>
                    <TextField select label="Modality" size="small" value={tmMod} onChange={e => setTmMod(e.target.value)}>
                      <MenuItem value="">— select modality —</MenuItem>
                      {(modalities.data ?? []).map(m => <MenuItem key={m.id} value={m.id}>{m.code} — {m.name}</MenuItem>)}
                    </TextField>
                    <Stack direction="row" spacing={1}>
                      <TextField label="Code" size="small" value={tmCode} onChange={e => setTmCode(e.target.value)} sx={{ width: 100 }} />
                      <TextField label="Name" size="small" value={tmName} onChange={e => setTmName(e.target.value)} sx={{ flexGrow: 1 }} />
                    </Stack>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Stack spacing={1}>
                    <TextField label="Technique (default)" size="small" value={tmTech} onChange={e => setTmTech(e.target.value)} />
                    <TextField label="Findings (default)" size="small" value={tmFind} onChange={e => setTmFind(e.target.value)} />
                    <TextField label="Impression (default)" size="small" value={tmImp} onChange={e => setTmImp(e.target.value)} />
                  </Stack>
                </Grid>
              </Grid>
              {createTemplate.isError && <Alert severity="error" sx={{ mt: 1 }}>{errorMessage(createTemplate.error)}</Alert>}
              <Button variant="outlined" sx={{ mt: 1 }}
                disabled={!tmMod || !tmCode.trim() || !tmName.trim() || createTemplate.isPending}
                onClick={() => createTemplate.mutate()}>Add template</Button>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                {(templates.data ?? []).map(t => (
                  <Typography key={t.id} variant="caption" color="text.secondary">{t.code} — {t.name}</Typography>
                ))}
              </Stack>
            </Paper>
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Draft report</Typography>
            <Stack spacing={1.5}>
              <TextField label="Study Id" size="small" value={rStudy} onChange={e => setRStudy(e.target.value)} />
              <TextField select label="Prefill from template" size="small" value={templateId} onChange={e => applyTemplate(e.target.value)}>
                <MenuItem value="">— none —</MenuItem>
                {(templates.data ?? []).map(t => <MenuItem key={t.id} value={t.id}>{t.code} — {t.name}</MenuItem>)}
              </TextField>
              <TextField label="Technique" size="small" multiline minRows={2} value={technique} onChange={e => setTechnique(e.target.value)}
                InputProps={{ endAdornment: micAdornment("technique", t => setTechnique(v => v + t)) }} />
              <TextField label="Findings" size="small" multiline minRows={2} value={findings} onChange={e => setFindings(e.target.value)}
                InputProps={{ endAdornment: micAdornment("findings", t => setFindings(v => v + t)) }} />
              <TextField label="Impression" size="small" multiline minRows={2} value={impression} onChange={e => setImpression(e.target.value)}
                InputProps={{ endAdornment: micAdornment("impression", t => setImpression(v => v + t)) }} />
              {!voice.supported && <Typography variant="caption" color="text.secondary">Voice dictation needs a Chromium-based browser.</Typography>}
              <Button variant="contained" onClick={() => draft.mutate()}
                disabled={!canDraft || draft.isPending || !rStudy || !findings || !impression}>Draft report</Button>
            </Stack>{err(draft)}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Approve / amend</Typography>
            <Stack spacing={1.5}>
              <TextField label="Report Id" size="small" value={reportId} onChange={e => setReportId(e.target.value)} />
              <Button variant="contained" onClick={() => approve.mutate()}
                disabled={!canApprove || approve.isPending || !reportId}>Approve report</Button>
              <TextField label="Addendum text" size="small" multiline minRows={2} value={addText} onChange={e => setAddText(e.target.value)} />
              <Button variant="outlined" onClick={() => addendum.mutate()}
                disabled={!canAmend || addendum.isPending || !reportId || !addText}>Add addendum</Button>
            </Stack>{err(approve, addendum)}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
